// Client-side column sorting for the BV list pages whose rows are already all in
// memory (Clients, Contacts). Sites sorts server-side because it pages through
// tens of thousands of NMIs; these lists don't, so the round trip buys nothing.
//
// Two rules the naive `a > b` version gets wrong and users notice immediately:
// blank cells belong at the bottom whichever way the column is pointing, and
// "Site 10" belongs after "Site 2".

export type SortDir = 'asc' | 'desc';

const isBlank = (v: unknown): boolean => v === null || v === undefined || v === '';

export const compareValues = (a: unknown, b: unknown, dir: SortDir): number => {
  // Blanks are ranked outside the direction — flipping a column shouldn't fill
  // the top of the list with empty cells.
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return 1;
  if (isBlank(b)) return -1;

  const flip = dir === 'asc' ? 1 : -1;

  if (typeof a === 'number' && typeof b === 'number') return (a - b) * flip;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    // Ticked first when ascending — "show me the Shield Platinum ones".
    return ((b ? 1 : 0) - (a ? 1 : 0)) * flip;
  }
  if (a instanceof Date && b instanceof Date) return (a.getTime() - b.getTime()) * flip;

  return String(a).localeCompare(String(b), 'en-AU', { numeric: true, sensitivity: 'base' }) * flip;
};

/** Sorted copy of `rows`; the original order is returned when no column is active. */
export const sortRows = <T>(
  rows: T[],
  accessor: ((row: T) => unknown) | null,
  dir: SortDir,
): T[] => {
  if (!accessor) return rows;
  // Array.prototype.sort is stable, so rows tied on the sorted column keep the
  // order the query returned them in.
  return [...rows].sort((a, b) => compareValues(accessor(a), accessor(b), dir));
};
